import React, { useEffect, useState } from 'react';
import { 
    Clock, 
    Users, 
    Monitor, 
    LogOut, 
    LogIn, 
    ShieldCheck, 
    Search,
    RefreshCw,
    Save,
    AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { API_BASE_URL } from '../../../../api';
import Swal from 'sweetalert2';
import "../Traitement/TraitementStyles/TraitementConges.css";

// v1.1 - Added extra safety for session filter
const SessionManagement = () => {
    const [sessionList, setSessionList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [timeoutMinutes, setTimeoutMinutes] = useState(30);
    const [timeoutUnit, setTimeoutUnit] = useState('MINUTES');
    const [warningValue, setWarningValue] = useState(5);
    const [warningUnit, setWarningUnit] = useState('MINUTES');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchSessions();
        fetchSettings();
    }, []);

    const fetchSessions = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE_URL}/api/admin/config/sessions`, {
                headers: { 'Authorization': `Bearer ${sessionStorage.getItem('token')}` }
            });
            
            const data = response.data;
            if (data && Array.isArray(data)) {
                setSessionList(data);
            } else {
                console.warn("Sessions data is not an array:", data);
                setSessionList([]);
            }
        } catch (error) {
            console.error("Erreur sessions:", error);
            setSessionList([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchSettings = async () => {
        try {
            const keys = ['SESSION_TIMEOUT', 'SESSION_TIMEOUT_UNIT', 'SESSION_TIMEOUT_WARNING', 'SESSION_TIMEOUT_WARNING_UNIT'];
            const requests = keys.map(key => 
                axios.get(`${API_BASE_URL}/api/admin/config/settings/${key}`, {
                    headers: { 'Authorization': `Bearer ${sessionStorage.getItem('token')}` }
                })
            );
            
            const results = await Promise.all(requests);
            
            if (results[0].data?.configValue) setTimeoutMinutes(parseInt(results[0].data.configValue));
            if (results[1].data?.configValue) setTimeoutUnit(results[1].data.configValue);
            if (results[2].data?.configValue) setWarningValue(parseInt(results[2].data.configValue));
            if (results[3].data?.configValue) setWarningUnit(results[3].data.configValue);
        } catch (error) {
            console.error("Erreur lors de la récupération des réglages", error);
        }
    };

    const convertToSeconds = (val, unit) => {
        const v = parseInt(val) || 0;
        if (unit === 'SECONDS') return v;
        if (unit === 'MINUTES') return v * 60;
        if (unit === 'HOURS') return v * 3600;
        return v * 60;
    };

    const handleSaveSettings = async () => {
        const totalSec = convertToSeconds(timeoutMinutes, timeoutUnit);
        const warnSec = convertToSeconds(warningValue, warningUnit);

        if (warnSec >= totalSec) {
            Swal.fire('Attention', 'Le délai de prévention doit être strictement inférieur à la durée totale de session.', 'warning');
            return;
        }

        try {
            setSaving(true);
            const settings = [
                { key: 'SESSION_TIMEOUT', val: timeoutMinutes.toString() },
                { key: 'SESSION_TIMEOUT_UNIT', val: timeoutUnit },
                { key: 'SESSION_TIMEOUT_WARNING', val: warningValue.toString() },
                { key: 'SESSION_TIMEOUT_WARNING_UNIT', val: warningUnit }
            ];

            await Promise.all(settings.map(s => 
                axios.post(`${API_BASE_URL}/api/admin/config/settings`, {
                    configKey: s.key,
                    configValue: s.val,
                    companyId: 1
                }, { headers: { 'Authorization': `Bearer ${sessionStorage.getItem('token')}` } })
            ));
            
            Swal.fire({
                icon: 'success',
                title: 'Enregistré',
                text: 'La configuration de sécurité a été mise à jour.',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            Swal.fire('Erreur', 'Impossible de sauvegarder la configuration.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const safeList = Array.isArray(sessionList) ? sessionList : [];
    const filteredSessions = safeList.filter(s => {
        if (!s || !s.user) return false;
        const name = `${s.user.firstName || ''} ${s.user.lastName || ''}`.toLowerCase();
        const email = (s.user.email || '').toLowerCase();
        const term = (searchTerm || '').toLowerCase();
        return name.includes(term) || email.includes(term);
    });

    return (
        <div className="tcvPage" id="session-management-v11">
            <div className="tcvWrap">
                <div className="tcvHeader">
                    <div>
                        <h1>Gestion des Sessions & Sécurité</h1>
                        <p>Surveillez les connexions actives et configurez les délais d'expiration</p>
                    </div>
                </div>


                {/* Search & Stats */}
                <div className="tcvCard">
                    <div className="tcvControls">
                        <div className="tcvSearch">
                            <Search size={18} />
                            <input
                                type="text"
                                placeholder="Rechercher un utilisateur..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button className="tcvFilterBtn" onClick={fetchSessions}>
                            <RefreshCw size={16} />
                            Actualiser
                        </button>
                    </div>
                </div>

                {/* Sessions Table */}
                <div className="tcvCard tcvCard--noPad">
                    <div className="tcvTableWrap">
                        {loading ? (
                            <div className="tcvEmpty">
                                <RefreshCw size={32} className="spin" />
                                <div>Chargement des sessions...</div>
                            </div>
                        ) : filteredSessions.length === 0 ? (
                            <div className="tcvEmpty">
                                <Users size={46} color="#94a3b8" />
                                <div>Aucune session trouvée</div>
                            </div>
                        ) : (
                            <table className="tcvTable">
                                <thead>
                                    <tr>
                                        <th>Utilisateur</th>
                                        <th>Début de session</th>
                                        <th>Fin de session</th>
                                        <th>Durée</th>
                                        <th>Appareil / IP</th>
                                        <th>Statut</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredSessions.map((s) => {
                                        const duration = s.logoutAt ? 
                                            Math.round((new Date(s.logoutAt) - new Date(s.loginAt)) / 60000) : 
                                            null;

                                        return (
                                            <tr key={s.id}>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7c3aed', fontWeight: 'bold' }}>
                                                            {s.user?.firstName?.[0]}{s.user?.lastName?.[0]}
                                                        </div>
                                                        <div>
                                                            <div className="tcvPrimary">{s.user?.firstName} {s.user?.lastName}</div>
                                                            <div className="tcvSub">{s.user?.email}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <LogIn size={14} color="#10b981" />
                                                        {new Date(s.loginAt).toLocaleString()}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <LogOut size={14} color={s.logoutAt ? "#ef4444" : "#94a3b8"} />
                                                        {s.logoutAt ? new Date(s.logoutAt).toLocaleString() : 'Active'}
                                                    </div>
                                                </td>
                                                <td>
                                                    {duration !== null ? (
                                                        <span style={{ fontWeight: '600' }}>{duration} min</span>
                                                    ) : (
                                                        <span style={{ color: '#10b981', fontWeight: '600' }}>En cours</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <div style={{ fontSize: '12px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                            <Monitor size={12} /> {s.ipAddress || '127.0.0.1'}
                                                        </div>
                                                        <div className="tcvSub" style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {s.userAgent || 'Navigateur standard'}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    {!s.logoutAt ? (
                                                        <span style={{ padding: '4px 8px', borderRadius: '12px', background: '#dcfce7', color: '#166534', fontSize: '11px', fontWeight: '700' }}>ACTIF</span>
                                                    ) : (
                                                        <span style={{ padding: '4px 8px', borderRadius: '12px', background: '#f1f5f9', color: '#475569', fontSize: '11px', fontWeight: '700' }}>TERMINÉ</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SessionManagement;
